import Typography from "@mui/material/Typography";
import { usePrevious } from "@uidotdev/usehooks";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AppContext } from "src/context/AppContext";
import RoutedButton from "src/newComponents/buttons/RoutedButton";
import CustomModal from "src/newComponents/CustomModal";

import { Icon } from "./_auth/components/Icon";

const ExitIntentPopup = () => {
  const { isAuthenticated } = useContext(AppContext);

  const { getExitIntentCount, getTotalExitIntentCount, bumpCount } = useExitIntentCount();
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();
  const previousPathname = usePrevious(location.pathname);

  useEffect(() => {
    // This function controls when to show the exit intent popup based on multiple conditions
    // It accepts a callback that determines if the exit trigger condition is met
    const handleExitIntent = (shouldTrigger: () => boolean) => {
      // If the user has seen the exit intent 5 times overall, don't show it again
      if (getTotalExitIntentCount() >= 5) return;

      // If the user has seen the exit intent 2 times in this session, don't show it again
      if (getExitIntentCount() >= 2) return;

      // If the exit intent is already open, don't show it again
      if (isOpen) return;

      if (shouldTrigger()) {
        // If the exit intent was shown in the last 5 minutes, don't show it again
        const lastShown = localStorage.getItem("exitIntentShownDateTime");
        if (lastShown) {
          const lastShownDate = new Date(lastShown);
          const now = new Date();
          const diff = now.getTime() - lastShownDate.getTime();
          const diffInMinutes = diff / 1000 / 60;
          if (diffInMinutes < 5) return;
        }

        bumpCount();
        setIsOpen(true);
        localStorage.setItem("exitIntentShownDateTime", new Date().toISOString());
      }
    };

    const onMouseOut = (e: MouseEvent) => handleExitIntent(() => !e.relatedTarget && e.clientY < 5);

    if (isAuthenticated) {
      window.removeEventListener("mouseout", onMouseOut);
      return;
    }

    // This block detects when a user navigates away from a courses page
    // But ignores if they're going to login or signup pages since that's a desired conversion
    if (location.pathname && previousPathname && !isAuthenticated) {
      if (location.pathname !== previousPathname) {
        if (previousPathname.startsWith("/courses") && !location.pathname.startsWith("/courses")) {
          // user is leaving the courses page
          // and going to a page that doesn't start with /courses

          const newPageUrl = location.pathname;
          if (newPageUrl.includes("login") || newPageUrl.includes("join-us")) {
            return;
          }

          handleExitIntent(() => true);
        }
      }
    }

    const isCoursesPage = location.pathname.startsWith("/courses");
    if (!isCoursesPage) return;

    // Only attach mouse exit detection on courses pages since that's where we want to capture exit intent
    window.addEventListener("mouseout", onMouseOut);

    return () => {
      window.removeEventListener("mouseout", onMouseOut);
    };
  }, [location, isAuthenticated]);

  return (
    <CustomModal isOpen={isOpen} handleClose={() => setIsOpen(false)}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Icon />
      </div>
      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Typography variant="h4">Wait! Don't miss out on your GPhC exam revision!</Typography>

        <Typography variant="body1">
          Join thousands of Trainees who have already started their GPhC exam revision and Foundation Pharmacist
          Training Year with Pre-Reg Shortcuts.
        </Typography>

        <RoutedButton path="/join-us/" buttonColor="inherit" size="large" onClick={() => setIsOpen(false)}>
          Join Us Now - It's FREE
        </RoutedButton>

        <Typography variant="body2">Create your free account in just 30 seconds.</Typography>
      </div>
    </CustomModal>
  );
};

export { ExitIntentPopup };

/**
 * Custom hook to manage exit intent counters
 * Uses localStorage for persistent total count across sessions
 * Uses sessionStorage for tracking counts within current session
 */
const useExitIntentCount = () => {
  const getTotalExitIntentCount = () => {
    const count = localStorage.getItem("totalExitIntentCount");
    return count ? parseInt(count) : 0;
  };

  const getExitIntentCount = () => {
    const count = sessionStorage.getItem("exitIntentCount");
    return count ? parseInt(count) : 0;
  };

  const bumpCount = () => {
    const newTotalExitIntentCount = getTotalExitIntentCount() + 1;
    localStorage.setItem("totalExitIntentCount", newTotalExitIntentCount.toString());

    const newExitIntentCount = getExitIntentCount() + 1;
    sessionStorage.setItem("exitIntentCount", newExitIntentCount.toString());
  };

  return { getTotalExitIntentCount, getExitIntentCount, bumpCount };
};
